import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import { makeStyles } from "@material-ui/styles";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";

import { TITLE_BUTTONS } from "../../constants/titles";
import { COLORS } from "../../constants/styles";
import {
  formTeamFields,
  GCP_BUCKET_URL_AVATARS,
  REQUIRED_FIELDS,
  TEAM_TYPE_OPTIONS,
  TIME_OUT_REQUEST,
} from "../../constants/data";
import {
  GET_SEARCH_VENUES,
  GET_STATES_BY_COUNTRY_ISO,
} from "../../constants/endpoints";

import { schema } from "../../utils/getSchemaTeamsForm";
import { getList } from "../../dataProvider/API";

import TextForm from "../../components/FormFields/TextForm";
import FileForm from "../../components/FormFields/FileForm";
import AutocompleteField from "../../components/FormFields/AutocompleteField";
import CountrySelect from "../../components/FormFields/CountrySelect";
import SelectCheckboxFilter from "../../components/FormFields/SelectCheckboxFilter";
import GoverningBodiesMultiSelect from "../../components/FormFields/GoverningBodiesMultiSelect";

const useStyles = makeStyles(() => ({
  submit: {
    width: "80px",
    height: "30px",
    backgroundColor: COLORS.BACKGROUND_GREEN,
    marginRight: "10px",
    "& p": {
      color: COLORS.TEXT_WHITE,
      fontSize: "0.8rem",
    },
  },
  cansel: {
    width: "80px",
    height: "30px",
    "& p": {
      fontSize: "0.8rem",
    },
  },
  fieldForm: {
    width: "350px",
  },
  logoWrapper: {
    width: "100%",
    height: "200px",
    marginBottom: "10px",
  },
  logoImage: {
    maxWidth: "100%",
    maxHeight: "100%",
    margin: "0 auto",
    display: "block",
  },
  actionsRow: {
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    width: "100%",
    display: "flex",
    justifyContent: "center",
    backgroundColor: "#fff",
    borderTop: "1px solid #e6e8eb",
    paddingTop: "8px",
    paddingBottom: "4px",
  },
}));

/**
 * Field name -> human label, walked from formTeamFields so it can't drift from the form.
 * Used to name missing fields in the invalid-submit toast.
 */
const FIELD_TITLES = (() => {
  const titles = {};

  Object.values(formTeamFields).forEach((entry) => {
    if (entry?.short && entry?.title) titles[entry.short] = entry.title;

    if (Array.isArray(entry?.data)) {
      entry.data.forEach(({ short, title }) => {
        if (short && title) titles[short] = title;
      });
    }
  });

  return titles;
})();

const TeamCreateForm = ({
  handleClose,
  onSubmit,
  defaultValue,
  isViewField,
  governingBodiesTitle,
  conference: conferenceList,
  logoPreview,
}) => {
  const classes = useStyles();

  /**
   * IMPORTANT:
   * API returns contactInformation nested:
   *   defaultValue.contactInformation.city, postalCode, country, etc
   * But the form fields are registered with FLAT names: "city", "postalCode", "country", etc.
   * So we flatten contactInformation into top-level defaults for RHF.
   */
  const normalizedDefaultValues = useMemo(() => {
    const dv = defaultValue || {};
    const ci = dv.contactInformation || {};

    return {
      ...dv,

      // flatten contactInformation fields
      ...ci,

      // ✅ ensure arrays exist
      governingBodies: dv.governingBodies || [],
      conference: dv.conference?.id ?? null,
      teamType: dv.teamType || "",

      // Keep selected venues from the team DTO; live search loads additional options.
      venues: Array.isArray(dv.venues) ? dv.venues : [],
    };
  }, [defaultValue]);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: normalizedDefaultValues,
  });

  /**
   * RHF defaultValues are used only on first render.
   * When you edit a different team, you must reset().
   * Use team id as the trigger to avoid wiping user edits on incidental re-renders.
   */
  useEffect(() => {
    if (!normalizedDefaultValues?.id) return;
    reset(normalizedDefaultValues);
  }, [normalizedDefaultValues?.id, reset, normalizedDefaultValues]);

  const [image, setImage] = useState(() => {
    if (logoPreview) return logoPreview;

    const alias = defaultValue?.cloudAlias;
    return alias ? `${GCP_BUCKET_URL_AVATARS}${alias}_master` : undefined;
  });
  const [isDisabled, setIsDisabled] = useState(false);
  const [states, setStates] = useState([]);

  // keep preview image in sync when switching teams
  useEffect(() => {
    // A logo supplied by the Google Maps autofill is inline base64 that has not been
    // uploaded yet, so `cloudAlias` is NOT a GCS object key. Building a bucket URL from
    // it would 404 and onErrorImage would wipe the field — show the inline image instead.
    if (logoPreview) {
      setImage(logoPreview);
      return;
    }

    const alias = defaultValue?.cloudAlias;
    if (!alias) {
      setImage(undefined);
      return;
    }
    setImage(`${GCP_BUCKET_URL_AVATARS}${alias}_master`);
  }, [defaultValue?.cloudAlias, logoPreview]);

  const governingBodies =
    watch("governingBodies") ?? normalizedDefaultValues.governingBodies;
  const country = watch("country") ?? normalizedDefaultValues.country;
  const state = watch("state") ?? normalizedDefaultValues.state;
  const conference = watch("conference") ?? normalizedDefaultValues.conference;

  const statesOptions = states?.map(({ name, abbreviation }) => ({
    id: abbreviation,
    label: name,
  }));

  const stateValue = state
    ? statesOptions.find((item) => item?.id?.trim() === state?.trim())
    : state;

  const conferenceOptions = conferenceList?.map(({ id, name }) => ({
    id,
    label: name,
  }));

  const conferenceValue = conference
    ? conferenceOptions.find((item) => item?.id === conference)
    : conference;
  const venueSearchConfig = useMemo(
    () => ({
      getEndpoint: (query) => GET_SEARCH_VENUES(query),
      params: {
        page: 0,
        limit: 25,
        sort: "Id",
        isAscending: true,
      },
      minSearchChars: 5,
      debounceMs: TIME_OUT_REQUEST,
    }),
    [],
  );

  const onChangeCountry = () => {
    setValue("state", null);
    setStates([]);
  };

  const onChange = (name) => {
    switch (name) {
      case "country":
        onChangeCountry();
        return;
      default:
        return;
    }
  };

  /**
   * Names the fields blocking submission.
   *
   * Without this, a failed validation is invisible: react-hook-form simply doesn't call
   * onSubmit, and this form is tall enough that the errors sit above the sticky action row
   * — so pressing FINISH looked like nothing happened at all. That bites hardest on a
   * Google Maps autofilled team, where the form appears complete but `abbreviation` and
   * `teamType` cannot be derived from a Maps place and are still empty.
   */
  const onInvalidSubmit = (formErrors) => {
    const missing = Object.keys(formErrors || {}).map(
      (name) => FIELD_TITLES[name] || name,
    );

    if (!missing.length) return;

    toast.error(`Please complete: ${missing.join(", ")}`);
  };

  const onErrorImage = () => {
    setImage(undefined);

    // Only a GCS-backed preview implies a stale alias worth clearing. An inline
    // autofill logo that fails to render must not silently drop the pending upload.
    if (logoPreview) return;

    setValue("cloudAlias", "");
  };

  useEffect(() => {
    const isFindGoverning = governingBodies?.find(
      (e) => e.abbreviation === "NCAA",
    );
    setIsDisabled(!isFindGoverning);
  }, [governingBodies]);

  useEffect(() => {
    if (!country) return;

    getList(GET_STATES_BY_COUNTRY_ISO(country)).then((res) => setStates(res));
  }, [country]);

  return (
    <React.Fragment>
      <Box>
        <form onSubmit={handleSubmit(onSubmit, onInvalidSubmit)}>
          <Grid
            container
            spacing={2}
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item md={12} className={classes.fieldForm}>
              <Controller
                render={({ field, fieldState }) => (
                  <TextForm
                    field={{
                      ...field,
                      required: formTeamFields.name.short === "name",
                    }}
                    label={formTeamFields.name.title}
                    size="small"
                    fieldState={fieldState}
                  />
                )}
                control={control}
                name={formTeamFields.name.short}
                defaultValue={
                  normalizedDefaultValues[formTeamFields.name.short] || ""
                }
              />
            </Grid>

            <Grid item md={12} className={classes.fieldForm}>
              <Controller
                render={({ field }) => (
                  <GoverningBodiesMultiSelect
                    field={field}
                    label={formTeamFields.governingBodies.title}
                    size="small"
                    initialOptions={governingBodiesTitle}
                  />
                )}
                control={control}
                name={formTeamFields.governingBodies.short}
                defaultValue={
                  normalizedDefaultValues[
                    formTeamFields.governingBodies.short
                  ] || []
                }
              />
            </Grid>

            {isViewField && (
              <Grid item md={12} className={classes.fieldForm}>
                <Controller
                  render={({ field, fieldState }) => (
                    <TextForm
                      field={field}
                      label={formTeamFields.coachEmail.title}
                      size={"small"}
                      fieldState={fieldState}
                      valueNormalizer={(value) => value?.toLowerCase()?.trim()}
                    />
                  )}
                  control={control}
                  name={formTeamFields.coachEmail.short}
                  defaultValue={
                    normalizedDefaultValues[formTeamFields.coachEmail.short] ||
                    ""
                  }
                />
              </Grid>
            )}

            <Grid item md={12} className={classes.fieldForm}>
              <Typography variant="h6">
                {formTeamFields.teamInfo.title}
              </Typography>
            </Grid>

            {formTeamFields.teamInfo.data.map(({ id, title, short }) => (
              <Grid item md={12} className={classes.fieldForm} key={id}>
                <Controller
                  render={({ field, fieldState }) => {
                    const props = {
                      field: {
                        ...field,
                        onChange: (value) => {
                          field.onChange(value);
                          onChange(short, value);
                        },
                      },
                      label: title,
                      size: "small",
                    };

                    const onChangeConference = (value) => {
                      field.onChange(value?.id ?? null);
                    };

                    switch (short) {
                      case "teamType":
                        // Required by the schema, so surface the asterisk and the error —
                        // this Select previously rendered neither, making a failed submit
                        // completely invisible.
                        return (
                          <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            required
                            error={fieldState.invalid}
                          >
                            <InputLabel>{title}</InputLabel>
                            <Select
                              {...props.field}
                              value={props.field?.value ?? ""}
                              onChange={(event) => field.onChange(event.target.value)}
                              label={title}
                            >
                              <MenuItem value="">
                                <em>Select team type</em>
                              </MenuItem>
                              {TEAM_TYPE_OPTIONS.map((option) => (
                                <MenuItem key={`team-type-${option.value}`} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {fieldState.error?.message ? (
                              <FormHelperText>
                                {fieldState.error.message}
                              </FormHelperText>
                            ) : null}
                          </FormControl>
                        );
                      case "conference":
                        return (
                          <AutocompleteField
                            label={title}
                            size={"small"}
                            data={conferenceOptions}
                            isDisabled={isDisabled}
                            field={{
                              ...props.field,
                              value: conferenceValue,
                              onChange: onChangeConference,
                            }}
                          />
                        );
                      default:
                        return (
                          <TextForm
                            {...props}
                            field={{
                              ...props.field,
                              required: REQUIRED_FIELDS.includes(field.name),
                            }}
                            fieldState={fieldState}
                          />
                        );
                    }
                  }}
                  control={control}
                  name={short}
                  defaultValue={
                    short === "teamType"
                      ? normalizedDefaultValues[short] ?? ""
                      : normalizedDefaultValues[short] || null
                  }
                />
              </Grid>
            ))}

            <Grid item md={12} className={classes.fieldForm}>
              <Typography variant="h6">
                {formTeamFields.contactInformation.title}
              </Typography>
            </Grid>

            {formTeamFields.contactInformation.data
              .filter(({ short }) =>
                short === "state" ? !!statesOptions?.length : true,
              )
              .map(({ id, short, title }) => (
                <Grid item md={12} className={classes.fieldForm} key={id}>
                  <Controller
                    render={({ field, fieldState }) => {
                      const props = {
                        field: {
                          ...field,
                          required: REQUIRED_FIELDS.includes(short),
                          onChange: (value) => {
                            field.onChange(value);
                            onChange(short, value);
                          },
                        },
                        label: title,
                        size: "small",
                        valueNormalizer: (value) => {
                          if (["email", "backupEmail"].includes(short))
                            return value?.toLowerCase()?.trim();
                          return value;
                        },
                      };

                      const onChangeState = (value) => {
                        field.onChange(value?.id || "");
                      };

                      switch (short) {
                        case "country":
                          return <CountrySelect {...props} />;
                        case "state":
                          return (
                            <AutocompleteField
                              {...props}
                              size="small"
                              field={{
                                ...props.field,
                                value: stateValue,
                                onChange: onChangeState,
                              }}
                              data={statesOptions}
                            />
                          );
                        default:
                          return (
                            <TextForm {...props} fieldState={fieldState} />
                          );
                      }
                    }}
                    control={control}
                    name={short}
                    defaultValue={normalizedDefaultValues[short] || null}
                  />
                </Grid>
              ))}

            <Grid item md={12} className={classes.fieldForm}>
              <Typography variant="h6">
                {formTeamFields.venues.title}
              </Typography>
            </Grid>

            <Grid item md={12} className={classes.fieldForm}>
              <Controller
                render={({ field }) => (
                  <SelectCheckboxFilter
                    field={field}
                    label={formTeamFields.venues.title}
                    size={"small"}
                    options={normalizedDefaultValues.venues}
                    searchConfig={venueSearchConfig}
                    helperText="Type at least 5 characters. First 25 matches are shown."
                  />
                )}
                control={control}
                name={formTeamFields.venues.short}
                defaultValue={
                  normalizedDefaultValues[formTeamFields.venues.short] || []
                }
              />
            </Grid>

            <Grid item md={12} className={classes.fieldForm}>
              <Typography variant="h6">
                {formTeamFields.cloudAlias.title}
              </Typography>
            </Grid>

            <Grid item md={12} className={classes.fieldForm}>
              {image && (
                <Box className={classes.logoWrapper}>
                  <img
                    src={image}
                    onError={onErrorImage}
                    className={classes.logoImage}
                    alt="logo"
                  />
                </Box>
              )}

              <Controller
                render={({ field }) => (
                  <FileForm
                    setImage={setImage}
                    field={field}
                    setValue={setValue}
                  />
                )}
                control={control}
                name={formTeamFields.cloudAlias.short}
                defaultValue={
                  normalizedDefaultValues[formTeamFields.cloudAlias.short] || ""
                }
              />
            </Grid>

            <Grid item className={classes.actionsRow}>
              <Button
                className={classes.submit}
                type="submit"
                variant="contained"
              >
                <Typography>{TITLE_BUTTONS.FINISH}</Typography>
              </Button>
              <Button
                className={classes.cansel}
                variant="outlined"
                onClick={handleClose}
              >
                <Typography>{TITLE_BUTTONS.CANCEL}</Typography>
              </Button>
            </Grid>
          </Grid>
        </form>
      </Box>
    </React.Fragment>
  );
};

TeamCreateForm.propTypes = {
  handleClose: PropTypes.func,
  onSubmit: PropTypes.func,
  options: PropTypes.array,
  defaultValue: PropTypes.object,
  isViewField: PropTypes.bool,
  governingBodiesTitle: PropTypes.array,
  data: PropTypes.array,
  conference: PropTypes.array,

  // Inline image src to show instead of deriving a GCS URL from cloudAlias.
  // Used when a logo has been imported but not yet uploaded.
  logoPreview: PropTypes.string,
};

TeamCreateForm.defaultProps = {
  logoPreview: undefined,
};

export default TeamCreateForm;
